import React, { useState, useEffect, useCallback } from 'react';
import { Boxes, Plus, Search, Edit, Trash2, Tag, Package, Wallet, TrendingUp, ShoppingCart, CheckCircle2, Globe, Sparkles, QrCode, Camera, AlertTriangle, X, MapPin } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';
import { confirmDialog } from '../lib/confirm';
import PartEditor from '../components/PartEditor';
import PartAdvertentie from '../components/PartAdvertentie';
import PartLabel from '../components/PartLabel';
import PartScanner from '../components/PartScanner';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const iconBtn = (kleur) => ({ width: 32, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: '1px solid var(--cs-border)', borderRadius: 6, background: '#fff', color: kleur, cursor: 'pointer' });

const CONDITIE_KLEUR = { 'Nieuw': '#16a34a', 'Zo goed als nieuw': '#16a34a', 'Gebruikt': '#2563eb', 'Refurbished': '#7c3aed', 'Defect / voor onderdelen': '#dc2626' };
const STATUS_KLEUR = { 'Voorraad': '#16a34a', 'Gereserveerd': '#f59e0b', 'Verkocht': '#64748b' };
const SHOWROOM_PUBLIEK = ['Gepubliceerd', 'Gereserveerd'];
const CATEGORIEEN = ['Verlichting', 'Carrosserie', 'Motor', 'Motor / elektrisch', 'Transmissie', 'Remsysteem', 'Ophanging', 'Stuurinrichting', 'Koeling', 'Wielen / banden', 'Interieur', 'Uitlaat', 'Overig'];

export default function OnderdelenVoorraad({ conditieFilter, titel }) {
  const { t } = useT();
  const { klanten, gegevens, refreshAll } = useData();
  const [parts, setParts] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState('');
  const [statusF, setStatusF] = useState('');
  const [editing, setEditing] = useState(null); // {} new | {part} edit
  const [adFor, setAdFor] = useState(null); // part voor AI-advertentie
  const [labelFor, setLabelFor] = useState(null); // part voor QR-label
  const [verkoopFor, setVerkoopFor] = useState(null); // part voor verkoop-modal
  const [scanNotFound, setScanNotFound] = useState(''); // gescande code zonder match
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showroomMap, setShowroomMap] = useState({}); // bronId -> listing
  const [selected, setSelected] = useState(new Set()); // geselecteerde part-ids
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkModal, setBulkModal] = useState(null); // 'locatie' | 'categorie'

  const onScan = (text) => {
    setScanning(false);
    const code = String(text || '').trim();
    if (!code) return;
    if (code.startsWith('MCPART:')) {
      const found = parts.find(p => p.id === code.slice(7));
      if (found) { setEditing(found); toast.success(t('Onderdeel gevonden', 'Onderdeel gevonden'), { description: found.naam }); }
      else { toast.error(t('Onderdeel niet in deze lijst', 'Onderdeel niet in deze lijst'), { description: t('Wis filters en probeer opnieuw', 'Wis filters en probeer opnieuw') }); }
    } else {
      // B: eerst zoeken op de gescande code; "nieuw" pas aanbieden als niets gevonden
      setScanNotFound(code);
      setQ(code);
      toast.success(t('Gescand', 'Gescand'), { description: code });
    }
  };

  const loadShowroom = useCallback(async () => {
    try {
      const listings = await carsysApi.getShowroomMine();
      const map = {};
      (listings || []).forEach(l => { if (l.bronId) map[l.bronId] = l; });
      setShowroomMap(map);
    } catch (e) { /* showroom optioneel (PRO) */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (conditieFilter) params.conditie = conditieFilter;
      if (q.trim()) params.q = q.trim();
      if (statusF) params.status = statusF;
      const [p, s] = await Promise.all([carsysApi.getParts(params), carsysApi.getPartsStats().catch(() => null)]);
      setParts(p); setStats(s);
      setSelected(new Set());
      loadShowroom();
    } catch (e) {
      toast.error(t('Laden mislukt', 'Laden mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [conditieFilter, q, statusF, t, loadShowroom]);

  useEffect(() => { const id = setTimeout(load, q ? 300 : 0); return () => clearTimeout(id); }, [load, q]);

  const isOnline = (p) => { const l = showroomMap[p.id]; return !!(l && SHOWROOM_PUBLIEK.includes(l.status)); };

  const toggleSel = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectableIds = parts.filter(p => p.status !== 'Verkocht').map(p => p.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));
  const clearSel = () => setSelected(new Set());

  const offline = async (p) => {
    const l = showroomMap[p.id];
    if (!l) return;
    try {
      await carsysApi.setShowroomStatus(l.id, 'Verborgen');
      toast.success(t('Offline gehaald', 'Offline gehaald'), { description: p.naam });
      loadShowroom();
    } catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const verwijder = async (p) => {
    if (!(await confirmDialog(`${t('Onderdeel verwijderen?', 'Onderdeel verwijderen?')}\n${p.naam}`))) return;
    await carsysApi.deletePart(p.id); toast.success(t('Verwijderd', 'Verwijderd')); load();
  };
  const verkoop = (p) => setVerkoopFor(p);
  const publiceer = async (p) => {
    try {
      const r = await carsysApi.publishShowroom({
        type: 'onderdeel', bronId: p.id, status: 'Gepubliceerd',
        titel: p.naam, prijs: p.vraagPrijs, naam: p.naam, conditie: p.conditie, aantal: p.aantal,
        oemNummer: p.oemNummer, onderdeelNummer: p.onderdeelNummer, afkomstigVoertuig: p.afkomstigVoertuig,
        bouwjaar: p.bouwjaar, kmStand: p.kmStand, motor: p.motor, kenteken: p.kenteken,
        pastOp: p.pastOp || [], oemCrossRef: p.oemCrossRef || [],
        categorie: p.categorie, aiTekst: p.opmerkingen || '', fotos: p.fotos || [],
      });
      toast.success(t('Gepubliceerd in showroom!', 'Gepubliceerd in showroom!'), { description: r.publicUrl });
      loadShowroom();
    } catch (e) { toast.error(t('Publiceren mislukt', 'Publiceren mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  // ---- Bulk-acties ----
  const selectedParts = () => parts.filter(p => selected.has(p.id));
  const runBulk = async (fn, klaarTekst) => {
    const items = selectedParts();
    if (items.length === 0) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const p of items) {
      try { await fn(p); ok++; } catch (e) { fail++; }
    }
    setBulkBusy(false);
    if (ok) toast.success(`${klaarTekst} (${ok})`, { description: fail ? `${fail} ${t('mislukt', 'mislukt')}` : undefined });
    else toast.error(t('Mislukt', 'Mislukt'));
    await load();
  };

  const bulkOnline = () => runBulk(
    (p) => carsysApi.publishShowroom({
      type: 'onderdeel', bronId: p.id, status: 'Gepubliceerd', titel: p.naam, prijs: p.vraagPrijs,
      naam: p.naam, conditie: p.conditie, aantal: p.aantal, oemNummer: p.oemNummer, onderdeelNummer: p.onderdeelNummer,
      afkomstigVoertuig: p.afkomstigVoertuig, bouwjaar: p.bouwjaar, kmStand: p.kmStand, motor: p.motor, kenteken: p.kenteken,
      pastOp: p.pastOp || [], oemCrossRef: p.oemCrossRef || [], categorie: p.categorie, aiTekst: p.opmerkingen || '', fotos: p.fotos || [],
    }),
    t('Online gezet', 'Online gezet'),
  );
  const bulkOffline = () => runBulk(async (p) => {
    const l = showroomMap[p.id];
    if (l) await carsysApi.setShowroomStatus(l.id, 'Verborgen');
  }, t('Offline gehaald', 'Offline gehaald'));
  const bulkVerkocht = async () => {
    if (!(await confirmDialog(`${t('Geselecteerde onderdelen markeren als verkocht?', 'Geselecteerde onderdelen markeren als verkocht?')} (${selected.size})`))) return;
    runBulk((p) => carsysApi.verkoopPart(p.id, {}), t('Verkocht', 'Verkocht'));
  };
  const bulkVerwijder = async () => {
    if (!(await confirmDialog(`${t('Geselecteerde onderdelen verwijderen?', 'Geselecteerde onderdelen verwijderen?')} (${selected.size})`))) return;
    runBulk((p) => carsysApi.deletePart(p.id), t('Verwijderd', 'Verwijderd'));
  };
  const bulkSetVeld = (veld, waarde) => runBulk((p) => carsysApi.updatePart(p.id, { [veld]: waarde }), t('Bijgewerkt', 'Bijgewerkt'));

  return (
    <div className="space-y-4" data-testid="onderdelen-voorraad-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Boxes size={22} /><div><h2 style={{ margin: 0 }}>  {titel ? t(titel, titel) : t('Voorraad', 'Voorraad')}</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--cs-text-muted)' }}>{parts.length} {t('onderdelen', 'onderdelen')}</p></div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cs-btn" onClick={() => setScanning(true)} data-testid="oh-scan"><Camera size={14} /> {t('Scannen', 'Scannen')}</button>
          <button className="cs-btn primary" onClick={() => setEditing({})} data-testid="oh-add-part"><Plus size={14} /> {t('Onderdeel toevoegen', 'Onderdeel toevoegen')}</button>
        </div>
      </div>

      {/* Dashboard-statistieken */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }} data-testid="oh-stats">
          <Kpi icon={<Package size={18} />} kleur="#2563eb" label={t('Op voorraad', 'Op voorraad')} val={stats.opVoorraad} testid="oh-kpi-voorraad" />
          <Kpi icon={<Wallet size={18} />} kleur="#16a34a" label={t('Voorraadwaarde (inkoop)', 'Voorraadwaarde (inkoop)')} val={eur(stats.voorraadwaarde)} testid="oh-kpi-waarde" />
          <Kpi icon={<TrendingUp size={18} />} kleur="#7c3aed" label={t('Verwachte waarde', 'Verwachte waarde')} val={eur(stats.verwachteWaarde)} testid="oh-kpi-verwacht" />
          {stats.onderMinimum > 0
            ? <Kpi icon={<AlertTriangle size={18} />} kleur="#dc2626" label={t('Onder minimum', 'Onder minimum')} val={stats.onderMinimum} testid="oh-kpi-minimum" />
            : <Kpi icon={<ShoppingCart size={18} />} kleur="#ea580c" label={t('Verkocht deze maand', 'Verkocht deze maand')} val={stats.verkochtDezeMaand} testid="oh-kpi-verkocht" />}
        </div>
      )}

      {/* Zoeken + filter */}
      <div className="cs-card padded">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: 11, color: '#94a3b8' }} />
            <input className="cs-input" style={{ paddingLeft: 32 }} placeholder={t('Zoek op naam, OEM, nummer, merk…', 'Zoek op naam, OEM, nummer, merk…')} value={q} onChange={e => setQ(e.target.value)} data-testid="oh-search" />
          </div>
          <select className="cs-input" style={{ maxWidth: 180 }} value={statusF} onChange={e => setStatusF(e.target.value)} data-testid="oh-status-filter">
            <option value="">{t('Alle statussen', 'Alle statussen')}</option>
            <option value="Voorraad">{t('Op voorraad', 'Op voorraad')}</option>
            <option value="Gereserveerd">{t('Gereserveerd', 'Gereserveerd')}</option>
            <option value="Verkocht">{t('Verkocht', 'Verkocht')}</option>
          </select>
        </div>

        {selected.size > 0 && (
          <div data-testid="oh-bulk-bar" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 12px', marginBottom: 12, background: 'var(--cs-card-alt, #f1f5f9)', border: '1px solid var(--cs-border)', borderRadius: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 13 }} data-testid="oh-bulk-count">{selected.size} {t('geselecteerd', 'geselecteerd')}</span>
            <span style={{ flex: 1 }} />
            <button className="cs-btn" disabled={bulkBusy} onClick={bulkOnline} data-testid="oh-bulk-online"><Globe size={14} /> {t('Online zetten', 'Online zetten')}</button>
            <button className="cs-btn" disabled={bulkBusy} onClick={bulkOffline} data-testid="oh-bulk-offline"><X size={14} /> {t('Offline halen', 'Offline halen')}</button>
            <button className="cs-btn" disabled={bulkBusy} onClick={bulkVerkocht} data-testid="oh-bulk-verkocht"><CheckCircle2 size={14} /> {t('Verkocht', 'Verkocht')}</button>
            <button className="cs-btn" disabled={bulkBusy} onClick={() => setBulkModal('locatie')} data-testid="oh-bulk-locatie"><MapPin size={14} /> {t('Locatie', 'Locatie')}</button>
            <button className="cs-btn" disabled={bulkBusy} onClick={() => setBulkModal('categorie')} data-testid="oh-bulk-categorie"><Tag size={14} /> {t('Categorie', 'Categorie')}</button>
            <button className="cs-btn danger" disabled={bulkBusy} onClick={bulkVerwijder} data-testid="oh-bulk-verwijder"><Trash2 size={14} /> {t('Verwijderen', 'Verwijderen')}</button>
            <button className="cs-btn ghost" disabled={bulkBusy} onClick={clearSel} data-testid="oh-bulk-clear" title={t('Selectie wissen', 'Selectie wissen')}><X size={14} /></button>
          </div>
        )}

        {loading ? <div className="cs-empty">{t('Laden…', 'Laden…')}</div> : parts.length === 0 ? (
          <div className="cs-empty" data-testid="oh-empty">
            {scanNotFound && q === scanNotFound ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 10 }}>{t('Geen onderdeel gevonden met code', 'Geen onderdeel gevonden met code')} <b style={{ fontFamily: 'ui-monospace, monospace' }}>{scanNotFound}</b></div>
                <button className="cs-btn primary" data-testid="oh-scan-new" onClick={() => { setEditing({ conditie: conditieFilter || 'Gebruikt', onderdeelNummer: scanNotFound }); }}>
                  <Plus size={14} /> {t('Nieuw onderdeel aanmaken met code', 'Nieuw onderdeel aanmaken met code')} {scanNotFound}
                </button>
              </div>
            ) : t('Nog geen onderdelen. Voeg er een toe of genereer ze vanuit een sloopauto.', 'Nog geen onderdelen. Voeg er een toe of genereer ze vanuit een sloopauto.')}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="cs-table" data-testid="oh-parts-table">
              <thead><tr>
                <th style={{ width: 34 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="oh-select-all" title={t('Alles selecteren', 'Alles selecteren')} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                </th>
                <th>{t('Onderdeel', 'Onderdeel')}</th><th>{t('Categorie', 'Categorie')}</th><th>{t('Conditie', 'Conditie')}</th>
                <th>{t('Locatie', 'Locatie')}</th><th>{t('Aantal', 'Aantal')}</th><th>{t('Vraagprijs', 'Vraagprijs')}</th>
                <th>{t('Status', 'Status')}</th><th></th>
              </tr></thead>
              <tbody>
                {parts.map(p => (
                  <tr key={p.id} data-testid={`oh-row-${p.id}`} style={selected.has(p.id) ? { background: 'var(--cs-blue-soft, #eff6ff)' } : undefined}>
                    <td>
                      {p.status !== 'Verkocht' && (
                        <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} data-testid={`oh-select-${p.id}`} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                      )}
                    </td><td><div style={{ fontWeight: 600 }}>{p.naam}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{p.afkomstigVoertuig || p.merk}</div></td>
                    <td>{p.categorie || '—'}</td>
                    <td><span style={{ fontSize: 11.5, fontWeight: 600, color: CONDITIE_KLEUR[p.conditie] || '#64748b' }}>{t(p.conditie, p.conditie)}</span></td>
                    <td>{p.magazijnLocatie || '—'}</td>
                    <td>
                      {p.aantal}
                      {Number(p.minVoorraad) > 0 && Number(p.aantal) <= Number(p.minVoorraad) && (
                        <span data-testid={`oh-lowstock-${p.id}`} title={`${t('Min.', 'Min.')} ${p.minVoorraad}`} style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 3, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 999, padding: '1px 7px', fontSize: 10.5, fontWeight: 700 }}>
                          <AlertTriangle size={11} /> {t('Bijbestellen', 'Bijbestellen')}
                        </span>
                      )}
                    </td>
                    <td>{eur(p.vraagPrijs)}</td>
                    <td><span style={{ fontSize: 11.5, fontWeight: 600, color: STATUS_KLEUR[p.status] || '#64748b' }}>{t(p.status,p.status)}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button style={iconBtn('#0f172a')} title={t('QR-label', 'QR-label')} onClick={() => setLabelFor(p)} data-testid={`oh-label-${p.id}`}><QrCode size={16} /></button>
                        {p.status !== 'Verkocht' && <button style={iconBtn('#a855f7')} title={t('AI-advertentie', 'AI-advertentie')} onClick={() => setAdFor(p)} data-testid={`oh-ad-${p.id}`}><Sparkles size={16} /></button>}
                        {p.status !== 'Verkocht' && (isOnline(p)
                          ? <button style={{ ...iconBtn('#fff'), background: '#16a34a', borderColor: '#16a34a' }} title={t('Online in showroom — klik om offline te halen', 'Online in showroom — klik om offline te halen')} onClick={() => offline(p)} data-testid={`oh-offline-${p.id}`}><Globe size={16} /></button>
                          : <button style={iconBtn('#7c3aed')} title={t('Publiceer in showroom', 'Publiceer in showroom')} onClick={() => publiceer(p)} data-testid={`oh-publish-${p.id}`}><Globe size={16} /></button>)}
                        {p.status !== 'Verkocht' && <button style={iconBtn('#16a34a')} title={t('Verkocht', 'Verkocht')} onClick={() => verkoop(p)} data-testid={`oh-sell-${p.id}`}><CheckCircle2 size={16} /></button>}
                        <button style={iconBtn('#475569')} title={t('Bewerken', 'Bewerken')} onClick={() => setEditing(p)} data-testid={`oh-edit-${p.id}`}><Edit size={16} /></button>
                        <button style={iconBtn('#dc2626')} title={t('Verwijderen', 'Verwijderen')} onClick={() => verwijder(p)} data-testid={`oh-del-${p.id}`}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <PartEditor part={editing.id ? editing : { conditie: conditieFilter || 'Gebruikt' }} onClose={() => setEditing(null)} onSaved={load} />}
      {adFor && <PartAdvertentie part={adFor} onClose={() => setAdFor(null)} />}
      {labelFor && <PartLabel part={labelFor} onClose={() => setLabelFor(null)} />}
      {scanning && <PartScanner onResult={onScan} onClose={() => setScanning(false)} />}
      {verkoopFor && <VerkoopModal part={verkoopFor} klanten={klanten || []} onClose={() => setVerkoopFor(null)} onDone={(res) => { load(); refreshAll && refreshAll(); }} />}
      {bulkModal && <BulkVeldModal type={bulkModal} locaties={gegevens?.locaties || []} onClose={() => setBulkModal(null)} onApply={async (waarde) => { const veld = bulkModal === 'locatie' ? 'magazijnLocatie' : 'categorie'; setBulkModal(null); await bulkSetVeld(veld, waarde); }} />}
    </div>
  );
}

const Kpi = ({ icon, kleur, label, val, testid }) => (
  <div data-testid={testid} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
    <div style={{ width: 38, height: 38, borderRadius: 9, background: `${kleur}1a`, color: kleur, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
    <div><div style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 600 }}>{label}</div><div style={{ fontSize: 18, fontWeight: 800 }}>{val}</div></div>
  </div>
);

function VerkoopModal({ part, klanten, onClose, onDone }) {
  const { t } = useT();
  const [verkoopprijs, setVerkoopprijs] = useState(part.vraagPrijs || 0);
  const [verzendkosten, setVerzendkosten] = useState(0);
  const [maakFactuur, setMaakFactuur] = useState(true);
  const [klantMode, setKlantMode] = useState('bestaand'); // 'bestaand' | 'nieuw'
  const [klantId, setKlantId] = useState('');
  const [nieuw, setNieuw] = useState({ naam: '', email: '', telefoon: '', adres: '', postcode: '', plaats: '' });
  const [busy, setBusy] = useState(false);
  const updN = (k, v) => setNieuw(p => ({ ...p, [k]: v }));

  const totaal = (Number(verkoopprijs) || 0) + (Number(verzendkosten) || 0);

  const submit = async () => {
    if (maakFactuur) {
      if (klantMode === 'bestaand' && !klantId) { toast.error(t('Kies een klant', 'Kies een klant')); return; }
      if (klantMode === 'nieuw' && !nieuw.naam.trim()) { toast.error(t('Vul de klantnaam in', 'Vul de klantnaam in')); return; }
    }
    setBusy(true);
    try {
      const payload = {
        verkoopprijs: Number(verkoopprijs) || 0, verzendkosten: Number(verzendkosten) || 0, maakFactuur,
        ...(maakFactuur ? (klantMode === 'nieuw' ? { nieuweKlant: nieuw } : { klantId }) : {}),
      };
      const res = await carsysApi.verkoopPartFactuur(part.id, payload);
      toast.success(t('Verkocht!', 'Verkocht!'), { description: res.bon ? `${t('Factuur', 'Factuur')} ${res.bon.nummer} · €${res.bon.totaal}` : undefined });
      onDone(res); onClose();
    } catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const inp = { width: '100%', padding: '8px 10px', border: '1px solid var(--cs-border)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const lab = { fontSize: 12, fontWeight: 600, color: 'var(--cs-text-muted)', display: 'block', margin: '8px 0 3px' };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()} data-testid="verkoop-modal">
        <div className="cs-modal-header">
          <h3><ShoppingCart size={16} /> {t('Onderdeel verkopen', 'Onderdeel verkopen')}</h3>
          <button className="cs-btn ghost" onClick={onClose}>✕</button>
        </div>
        <div className="cs-modal-body">
          <div style={{ background: 'var(--cs-card-alt, #f8fafc)', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ fontWeight: 700 }}>{part.naam}</div>
            <div style={{ fontSize: 12, color: 'var(--cs-text-muted)' }}>{part.afkomstigVoertuig || part.merk || ''}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lab}>{t('Verkoopprijs (€)', 'Verkoopprijs (€)')}</label>
              <input type="number" step="0.01" style={inp} value={verkoopprijs} onChange={e => setVerkoopprijs(e.target.value)} data-testid="verkoop-prijs" /></div>
            <div><label style={lab}>{t('Verzendkosten (€)', 'Verzendkosten (€)')}</label>
              <input type="number" step="0.01" style={inp} value={verzendkosten} onChange={e => setVerzendkosten(e.target.value)} data-testid="verkoop-verzend" placeholder="0,00" /></div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 0 4px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <input type="checkbox" checked={maakFactuur} onChange={e => setMaakFactuur(e.target.checked)} data-testid="verkoop-maakfactuur" />
            {t('Factuur aanmaken', 'Factuur aanmaken')}
          </label>

          {maakFactuur && (
            <div style={{ border: '1px solid var(--cs-border)', borderRadius: 8, padding: 12, marginTop: 6 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <button className={`cs-btn ${klantMode === 'bestaand' ? 'primary' : ''}`} style={{ flex: 1 }} onClick={() => setKlantMode('bestaand')} data-testid="verkoop-klant-bestaand">{t('Bestaande klant', 'Bestaande klant')}</button>
                <button className={`cs-btn ${klantMode === 'nieuw' ? 'primary' : ''}`} style={{ flex: 1 }} onClick={() => setKlantMode('nieuw')} data-testid="verkoop-klant-nieuw">{t('Nieuwe klant', 'Nieuwe klant')}</button>
              </div>
              {klantMode === 'bestaand' ? (
                <select style={inp} value={klantId} onChange={e => setKlantId(e.target.value)} data-testid="verkoop-klant-select">
                  <option value="">-- {t('Kies klant', 'Kies klant')} --</option>
                  {klanten.map(k => <option key={k.id} value={k.id}>{k.naam}{k.plaats ? ` · ${k.plaats}` : ''}</option>)}
                </select>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div style={{ gridColumn: '1 / 3' }}><label style={lab}>{t('Naam', 'Naam')} *</label><input style={inp} value={nieuw.naam} onChange={e => updN('naam', e.target.value)} data-testid="verkoop-nieuw-naam" /></div>
                  <div><label style={lab}>E-mail</label><input style={inp} value={nieuw.email} onChange={e => updN('email', e.target.value)} /></div>
                  <div><label style={lab}>{t('Telefoon', 'Telefoon')}</label><input style={inp} value={nieuw.telefoon} onChange={e => updN('telefoon', e.target.value)} /></div>
                  <div style={{ gridColumn: '1 / 3' }}><label style={lab}>{t('Adres', 'Adres')}</label><input style={inp} value={nieuw.adres} onChange={e => updN('adres', e.target.value)} /></div>
                  <div><label style={lab}>{t('Postcode', 'Postcode')}</label><input style={inp} value={nieuw.postcode} onChange={e => updN('postcode', e.target.value)} /></div>
                  <div><label style={lab}>{t('Plaats', 'Plaats')}</label><input style={inp} value={nieuw.plaats} onChange={e => updN('plaats', e.target.value)} /></div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, fontSize: 16, fontWeight: 800 }}>
            <span>{t('Totaal', 'Totaal')}</span><span>{eur(totaal)}</span>
          </div>
        </div>
        <div className="cs-modal-footer">
          <button className="cs-btn" onClick={onClose}>{t('Annuleren', 'Annuleren')}</button>
          <button className="cs-btn primary" onClick={submit} disabled={busy} data-testid="verkoop-confirm">
            {busy ? '...' : (maakFactuur ? t('Verkoop & factuur', 'Verkoop & factuur') : t('Markeer verkocht', 'Markeer verkocht'))}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkVeldModal({ type, locaties, onClose, onApply }) {
  const { t } = useT();
  const isLoc = type === 'locatie';
  const opties = isLoc ? locaties : CATEGORIEEN;
  const [waarde, setWaarde] = useState(isLoc ? '' : (opties[0] || ''));
  const [eigen, setEigen] = useState('');

  const apply = () => {
    const v = (isLoc && waarde === '__eigen__') ? eigen.trim() : waarde;
    if (!v) { toast.error(t('Kies of vul een waarde in', 'Kies of vul een waarde in')); return; }
    onApply(v);
  };

  const inp = { width: '100%', padding: '8px 10px', border: '1px solid var(--cs-border)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()} data-testid="oh-bulk-veld-modal">
        <div className="cs-modal-header">
          <h3>{isLoc ? <MapPin size={16} /> : <Tag size={16} />} {isLoc ? t('Locatie wijzigen', 'Locatie wijzigen') : t('Categorie wijzigen', 'Categorie wijzigen')}</h3>
          <button className="cs-btn ghost" onClick={onClose}>✕</button>
        </div>
        <div className="cs-modal-body">
          <p style={{ marginTop: 0, fontSize: 13, color: 'var(--cs-text-muted)' }}>
            {isLoc ? t('Stel de magazijnlocatie in voor alle geselecteerde onderdelen.', 'Stel de magazijnlocatie in voor alle geselecteerde onderdelen.')
                   : t('Stel de categorie in voor alle geselecteerde onderdelen.', 'Stel de categorie in voor alle geselecteerde onderdelen.')}
          </p>
          <select style={inp} value={waarde} onChange={e => setWaarde(e.target.value)} data-testid="oh-bulk-veld-select">
            {isLoc && <option value="">-- {t('Kies locatie', 'Kies locatie')} --</option>}
            {opties.map(o => <option key={o} value={o}>{o}</option>)}
            {isLoc && <option value="__eigen__">{t('Andere locatie…', 'Andere locatie…')}</option>}
          </select>
          {isLoc && waarde === '__eigen__' && (
            <input style={{ ...inp, marginTop: 8 }} placeholder={t('Bijv. Schap 3', 'Bijv. Schap 3')} value={eigen} onChange={e => setEigen(e.target.value)} data-testid="oh-bulk-veld-eigen" />
          )}
        </div>
        <div className="cs-modal-footer">
          <button className="cs-btn" onClick={onClose}>{t('Annuleren', 'Annuleren')}</button>
          <button className="cs-btn primary" onClick={apply} data-testid="oh-bulk-veld-apply">{t('Toepassen', 'Toepassen')}</button>
        </div>
      </div>
    </div>
  );
}

